/* eslint-disable max-lines -- widget wires turbo v1/v2 UI, comms, and slide-to-pay in one tree */
import { ApiResponseJuspayMeta, VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";
//defs
import {
    CredEligibleData,
    PaymentGroupActions,
    PaymentMethodDowntime,
    TurboAutoPayMethod,
    TurboPayOfferComms,
    TurboPaymentMethod,
    TurboPaymentSpecificActions,
    TurboPaymentVariant,
    TurboPodHalfCardEvents,
    TurboVariantProps,
} from "@payment-ui/interfaces";
import { TurboStripData, TurboStripEventAction } from "@swiggy-private/core-ui";
import { ACCESSIBILITY_ROLES } from "@swiggy-private/core-types";
//deps
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
// contexts
import { TurboPaymentContext, TurboPaymentContextValue } from "./TurboPaymentContext";

import TurboPaymentDowntimeWidget from "./components/turbo-payment-downtime-widget";
import TurboPaymentMethodWidget from "./components/turbo-payment-method-widget";
import QuickCheckoutTurboPaymentMethodWidget from "./components/turbo-payment-method-widget/v2/QuickCheckoutTurboPaymentMethodWidget";
import TurboPaymentPreloader from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-preloader/TurboPaymentPreloader";
//components
import TurboPaymentWidgetComms from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-widget-comms/TurboPaymentWidgetComms";
import TurboPaymentWidgetCommsV2 from "./components/turbo-payment-widget-comms-v2/TurboPaymentWidgetCommsV2";
import TurboPaymentWidgetShimmer from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-widget-shimmer/TurboPaymentWidgetShimmer";
import TurboPaymentWidgetShimmerV2 from "@payment-ui/components/turbo-payment-widget/components/turbo-payment-widget-shimmer/v2/TurboPaymentWidgetShimmer";
import { getCommsToShow, getQuickCheckoutVariantData, isQuickCheckoutVariant } from "@payment-ui/helpers/TurboPayment";
//constants
import { TURBO_WIDGET_GROUP_ARIA_LABEL } from "@payment-ui/constants/TurboPayment";
//helpers
import { getTurboPayCtaTextProps } from "@payment-ui/helpers/Upi";
// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { showTurboTooltip } from "@payment-ui/helpers/TurboTooltip";
import { ProceedMode, useLocalStorageBasedFrequencyCapping, NOOP_OBJECT } from "@swiggy-private/checkout-core";
import { getTurboTooltipViewFreqCappPayload } from "./constants";

type TurboPaymentWidgetProps = TurboVariantProps & {
    loading: boolean;
    onClickChangePaymentCta: VoidFunction;
    paymentMethod?: TurboPaymentMethod | null;
    billAmount: string;
    paymentAmountWithoutDiscount: string;
    getTurboPaymentSpecificActions: (groupName: string) => TurboPaymentSpecificActions;
    hideEducationComms: boolean;
    juspayMeta?: ApiResponseJuspayMeta;
    showOverlay: boolean;
    onSlideButtonDrag: TurboPaymentContextValue["onSlideButtonDrag"];
    onSlideButtonSuccess: TurboPaymentContextValue["onSlideButtonSuccess"];
    onImpressionChangeTooltipAnimation: VoidFunction;
    onClickChangeTooltipAnimation: VoidFunction;
    showDiscountedAmount: boolean;
    balanceValue?: string | number; // for wallets + swiggy-money
    credEligibleData?: CredEligibleData;
    turboPaymentOfferComms: TurboPayOfferComms;
    paymentMethodDowntime?: PaymentMethodDowntime;
    onPaymentDowntimeWidgetLoad: VoidFunction;
    autoPaymethod: TurboAutoPayMethod;
    turboPodHalfCardEvents: TurboPodHalfCardEvents;
    manageSwipe: (disable: boolean, height?: number) => void;
    increaseSliderWidth: boolean;
    eventOnImpressionTooltip?: (stripMsg?: string) => void;
    eventOnTooltipClose?: VoidFunction;
    tooltipDetails?: {
        text?: string;
        enabled?: boolean;
        key?: string;
        icon?: string;
        maxViewFrequency?: number;
    };
    isTurboV2?: boolean;
    isPopCart?: boolean;
    onClickTurboStrip?: TurboStripEventAction;
    onImpressionTurboStrip?: TurboStripEventAction;
    turboStrip?: TurboStripData;
    cartCallbackOnTurboPaymentChange?: VoidFunction;
    billAmountShownOnCart?: string;
    uniqueId?: string;
    amountWithDiscount: string;
    isBillToCompanyExist?: boolean;
    clampDiscountText?: boolean;
    onCvvPopupOpen?: VoidFunction;
    keyFactStatementUrl?: string;
    onKeyFactStatementClick?: (url: string) => void;
    /**
     * Skips the one-time education tooltip without writing the edu localStorage flag.
     * Callers that own route context (e.g. Quick Checkout footer) should set this; payment-ui
     * stays route-agnostic.
     */
    disableEduComms?: boolean;
    /**
     * QC only. Cart updates keep the widget mounted with an inline loading state, so the shimmer
     * is limited to the very first load.
     */
    isLoadingForTheFirstTime?: boolean;
};

const CHANGE_CTA_TOOLTIP_TRIGGER_TIME = 5000;

const TurboPaymentWidget = (props: TurboPaymentWidgetProps): JSX.Element | null => {
    const {
        paymentMethod,
        onClickChangePaymentCta,
        paymentAmountWithoutDiscount,
        loading,
        getTurboPaymentSpecificActions,
        hideEducationComms,
        showOverlay,
        onSlideButtonDrag,
        onImpressionChangeTooltipAnimation,
        onClickChangeTooltipAnimation,
        onSlideButtonSuccess,
        balanceValue,
        credEligibleData,
        showDiscountedAmount,
        turboPaymentOfferComms,
        paymentMethodDowntime,
        onPaymentDowntimeWidgetLoad,
        autoPaymethod,
        turboPodHalfCardEvents,
        manageSwipe,
        increaseSliderWidth,
        eventOnImpressionTooltip,
        eventOnTooltipClose,
        tooltipDetails,
        isTurboV2 = false,
        isPopCart,
        onClickTurboStrip,
        onImpressionTurboStrip,
        turboStrip = {},
        cartCallbackOnTurboPaymentChange,
        amountWithDiscount,
        isBillToCompanyExist,
        clampDiscountText = false,
        onCvvPopupOpen,
        keyFactStatementUrl,
        onKeyFactStatementClick,
        disableEduComms = false,
        isLoadingForTheFirstTime = false,
        variant = TurboPaymentVariant.DEFAULT,
        variantData = NOOP_OBJECT,
    } = props;

    const isQuickCheckout = isQuickCheckoutVariant(variant);
    // Kept as a primitive so the method-widget memo below is not busted by the variantData identity.
    const quickCheckoutItemCount = getQuickCheckoutVariantData(variant, variantData)?.totalItemCount;

    const turboWidgetRef = useRef<HTMLDivElement>(null);

    const { shouldProceed: shouldRenderCustomTooltip } = useLocalStorageBasedFrequencyCapping(
        getTurboTooltipViewFreqCappPayload(tooltipDetails?.key, tooltipDetails?.maxViewFrequency),
    );

    const paymentMethodUi = useMemo(() => {
        if (!paymentMethod) {
            return null;
        }

        if (!isEmpty(paymentMethodDowntime) && !isEmpty(paymentMethodDowntime?.singleClickPresentationInfo)) {
            return (
                <TurboPaymentDowntimeWidget
                    methodData={autoPaymethod}
                    paymentMethodDowntime={paymentMethodDowntime}
                    paymentActions={
                        getTurboPaymentSpecificActions(
                            VALID_PAYMENT_GROUP_TYPES.COD,
                        ) as PaymentGroupActions<TurboPaymentMethod>
                    }
                    onClickChangePaymentCta={onClickChangePaymentCta}
                    showDiscountedAmount={showDiscountedAmount}
                    amountWithDiscount={amountWithDiscount}
                    amountWithoutDiscount={paymentAmountWithoutDiscount}
                    onLoad={onPaymentDowntimeWidgetLoad}
                />
            );
        }

        const groupName = paymentMethod?.groupName?.toUpperCase();

        const turboPaymentCtaMeta = getTurboPayCtaTextProps({
            amountWithDiscount,
            amountWithoutDiscount: paymentAmountWithoutDiscount,
            methodData: paymentMethod,
        });

        const paymentActions = getTurboPaymentSpecificActions(groupName) as PaymentGroupActions<TurboPaymentMethod>;

        if (isQuickCheckout) {
            return (
                <QuickCheckoutTurboPaymentMethodWidget
                    methodData={paymentMethod}
                    turboPaymentCtaMeta={turboPaymentCtaMeta}
                    paymentActions={paymentActions}
                    onClickChangePaymentCta={onClickChangePaymentCta}
                    balanceValue={balanceValue}
                    credEligibleData={credEligibleData}
                    showDiscountedAmount={showDiscountedAmount}
                    increaseSliderWidth={increaseSliderWidth}
                    clampDiscountText={clampDiscountText}
                    isLoading={loading}
                    isLoadingForTheFirstTime={isLoadingForTheFirstTime}
                    totalItemCount={quickCheckoutItemCount}
                />
            );
        }

        return (
            <TurboPaymentMethodWidget
                methodData={paymentMethod}
                turboPaymentCtaMeta={turboPaymentCtaMeta}
                paymentActions={paymentActions}
                onClickChangePaymentCta={onClickChangePaymentCta}
                balanceValue={balanceValue}
                credEligibleData={credEligibleData}
                showDiscountedAmount={showDiscountedAmount}
                turboPodHalfCardEvents={turboPodHalfCardEvents}
                increaseSliderWidth={increaseSliderWidth}
                isTurboV2={isTurboV2}
                isPopCart={isPopCart}
                onClickTurboStrip={onClickTurboStrip}
                onImpressionTurboStrip={onImpressionTurboStrip}
                turboStrip={turboStrip}
                isBillToCompanyExist={isBillToCompanyExist}
                clampDiscountText={clampDiscountText}
                onCvvPopupOpen={onCvvPopupOpen}
                keyFactStatementUrl={keyFactStatementUrl}
                onKeyFactStatementClick={onKeyFactStatementClick}
            />
        );
    }, [
        paymentMethod,
        paymentMethodDowntime,
        amountWithDiscount,
        paymentAmountWithoutDiscount,
        getTurboPaymentSpecificActions,
        onClickChangePaymentCta,
        balanceValue,
        credEligibleData,
        showDiscountedAmount,
        turboPodHalfCardEvents,
        increaseSliderWidth,
        isTurboV2,
        isPopCart,
        onClickTurboStrip,
        onImpressionTurboStrip,
        turboStrip,
        isBillToCompanyExist,
        clampDiscountText,
        autoPaymethod,
        onPaymentDowntimeWidgetLoad,
        onCvvPopupOpen,
        keyFactStatementUrl,
        onKeyFactStatementClick,
        isQuickCheckout,
        isLoadingForTheFirstTime,
        loading,
        quickCheckoutItemCount,
    ]);

    const [showChangeAnimation, setShowChangeAnimation] = useState(false);

    const primaryToolipDismissCallback = useCallback(() => {
        if (isQuickCheckout) {
            // QC skips Change-CTA education tooltip (`showTurboCommsV2` already off for QC).
            return;
        }
        // Change CTA tooltip should be triggered exactly 5s after the primary slider tooltip was triggered
        setTimeout(() => {
            setShowChangeAnimation(true);
        }, CHANGE_CTA_TOOLTIP_TRIGGER_TIME);
    }, [isQuickCheckout]);

    const commsToShow = useMemo(() => {
        return loading ? null : getCommsToShow(turboPaymentOfferComms?.showTooltip, { disableEduComms });
    }, [turboPaymentOfferComms?.showTooltip, loading, disableEduComms]);

    // Excluded hideEducationComms in deps array
    // because tooltip should not be visible for session which has generic crouton
    const _showTurboTooltip = useMemo(() => {
        return (
            showTurboTooltip(commsToShow, hideEducationComms) &&
            tooltipDetails?.enabled &&
            shouldRenderCustomTooltip({ mode: ProceedMode.CHECK_AND_UPDATE })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commsToShow, tooltipDetails?.enabled, shouldRenderCustomTooltip]);

    const showTurboCommsV2 = !isQuickCheckout && (showChangeAnimation || _showTurboTooltip);

    useEffect(() => {
        if (!turboWidgetRef.current || !increaseSliderWidth) {
            return;
        }

        // using offsetHeight to include the borders (if any)
        const height = turboWidgetRef.current?.offsetHeight;
        manageSwipe(true, height);
    }, [turboWidgetRef, manageSwipe, loading, increaseSliderWidth]);

    useEffect(() => {
        if (typeof cartCallbackOnTurboPaymentChange === "function" && !isEmpty(paymentMethodUi) && !loading) {
            cartCallbackOnTurboPaymentChange?.();
        }
    }, [cartCallbackOnTurboPaymentChange, paymentMethodUi, loading]);

    // QC shows the shimmer on first load only; later cart updates render the widget with an
    // inline loading state instead of swapping it out.
    if (loading && (!isQuickCheckout || isLoadingForTheFirstTime)) {
        if (isTurboV2) {
            return <TurboPaymentWidgetShimmerV2 />;
        }
        return <TurboPaymentWidgetShimmer />;
    }

    return (
        <TurboPaymentContext.Provider
            value={{
                onSlideButtonDrag,
                onSlideButtonSuccess,
                showChangeAnimation,
            }}
        >
            <div ref={turboWidgetRef} role={ACCESSIBILITY_ROLES.GROUP} aria-label={TURBO_WIDGET_GROUP_ARIA_LABEL}>
                {showOverlay && <TurboPaymentPreloader />}

                {showTurboCommsV2 && (
                    <TurboPaymentWidgetCommsV2
                        onImpressionChangeTooltipAnimation={onImpressionChangeTooltipAnimation}
                        onClickChangeTooltipAnimation={onClickChangeTooltipAnimation}
                        isChangeModeAnimation={showChangeAnimation}
                        eventOnImpressionTooltip={eventOnImpressionTooltip}
                        eventOnTooltipClose={eventOnTooltipClose}
                        tooltipDetails={tooltipDetails}
                        showTurboTooltip={_showTurboTooltip}
                        isTurboV2={isTurboV2}
                    />
                )}
                <TurboPaymentWidgetComms
                    hideEducationComms={hideEducationComms}
                    primaryToolipDismissCallback={primaryToolipDismissCallback}
                    turboPaymentOfferComms={turboPaymentOfferComms}
                    commsToShow={commsToShow}
                    method={paymentMethod?.name}
                    isTurboV2={isTurboV2}
                />
                {paymentMethodUi}
            </div>
        </TurboPaymentContext.Provider>
    );
};

export default memo(TurboPaymentWidget);
